import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import {
  FETCH_PREDEFINED_ORDERS,
  FETCH_SUB_CAPITOLS,
} from '../../../../gql/queries/orders/predefinedData';
import { GET_SELECTORS_DATA } from '../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_CLASSIFICATIONS,
} from '../../../../gql/queries/orders/predefinedData';

// Type
import { AuthContextType } from '../../../../hooks/useAuth';

// Data static
import {
  groupLanes,
  groupTracks,
  specialTagType,
} from '../../../../data/orders';

// Compression
import { selectCommunicationOrderByIdOrderWithRelationshipIds } from '../../../../gql/selectors/communications';

// Redux
import { store } from '../../../../state/configureStore';
import { tabs } from '../../../../state/actions/ui/tabs';
import { tabsType } from '../../../../state/reducers/ui/tabs';
import { OrderType } from '../../../../types/orders';

// Dueño de la copia (S2/S3, auditoría 2026-11-02)
import { claimDraftScope, isDraftOwner } from '../../../../utils/draftOwnership';
import { COMMUNICATION_EDIT_SCOPE } from '../../../../utils/draftScopes';
import { readOwnCommunicationEditCopy } from '../../../../utils/communicationEditDraft';

const loadEditCommunicationSummary = async (
  idOrder: string,
  userData: AuthContextType | null
) => {
  try {
    const orderCommunicationData =
      await selectCommunicationOrderByIdOrderWithRelationshipIds(idOrder);
    return {
      ...orderCommunicationData,
      operator: `${userData?.user?.user?.name} ${
        userData?.user?.user?.surName.split(' ')[0]
      }`,
    };
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Communication Summary loading error:', err);
    return true;
  }
};

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      const [
        { data },
        {
          data: { capitols },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { classifications },
        },
        {
          data: { subCapitols },
        },
      ] = await Promise.all([
        client.query({
          query: GET_SELECTORS_DATA,
        }),
        client.query({
          query: FETCH_CAPITOLS,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
        client.query({
          query: FETCH_SUB_CAPITOLS,
        }),
      ]);

      return {
        ...data,
        capitols,
        predefinedOrders,
        classifications,
        groupTracks,
        groupLanes,
        specialTagType,
        subCapitols,
      };
    }
    return {};
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Communication Edit summary loading error:', err);
    return true;
  }
};

export const EditCommunicationLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined =
  (userData) =>
  async ({ params }) => {
  // Se reclama SÍNCRONO, antes de cualquier `await`: la copia de otra
  // comunicación (atrás, URL a mano, deep link) se descarta aquí y nunca llega
  // a montarse sobre la de esta URL (S2/S3).
  claimDraftScope(COMMUNICATION_EDIT_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    // La copia solo si es de ESTA comunicación; si no, manda el servidor. Se
    // lee antes de los `await` (como antes), con el dueño recién reclamado.
    const sessionStorageData = readOwnCommunicationEditCopy(params.id);

    let dataFromCommunicationToEdit: any;
    if (params.id) {
      dataFromCommunicationToEdit = await loadEditCommunicationSummary(
        params.id,
        userData
      );
    }

    const dispatch = store.dispatch;

    const loaderToResolve = await loadFormOptions(userData);

    let defaultTab = 1;

    if (dataFromCommunicationToEdit?.parentOrderType === OrderType.Campaign) {
      defaultTab = 2;
    }

    // La pestaña es estado global: si tras los `await` otra comunicación ha
    // reclamado el ámbito (el usuario ya está en B), el loader tardío de A no
    // debe cambiarle la pestaña a B (auditoría § 3).
    if (!params.id || isDraftOwner(COMMUNICATION_EDIT_SCOPE, params.id)) {
      dispatch(tabs(tabsType.EDIT_COMMUNICATION, defaultTab));
    }

    if (sessionStorageData) {

      const combinedData = {
        // team field removed - users no longer have teamIDs
        ...loaderToResolve,
        ...dataFromCommunicationToEdit,
        ...sessionStorageData,
      };

      return combinedData;
    }

    const nameOperator = `${userData?.user?.user.name} ${
      userData?.user?.user.surName.split(' ')[0]
    }`;
    const combinedData = {
      // team field removed - users no longer have teamIDs
      ...loaderToResolve,
      ...dataFromCommunicationToEdit,
      operator: nameOperator,
    };

    return combinedData;
  };

  return { data: load() };
};
